import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as maplibregl from "maplibre-gl";
import type { ExpressionSpecification, PaddingOptions } from "maplibre-gl";
import { useTranslation } from "react-i18next";
import type { Feature, FeatureCollection, GeoJsonProperties, Geometry, Position } from "geojson";
import { fetchScaledMapData, prefetchFloors } from "./utils/mapDataUtils";
import { useMapInitialization } from "./hooks/useMapInitialization";
import { useFloorState } from "./hooks/useFloorState";
import MapFloorSelector from "./components/MapFloorSelector";
import MapFloorSelectorButtonSm from "@/components/MapFloorSelectorButtonSm";
import styles from "./styles/App.module.css";
import { useCurrentRoute } from "@/features/pathFinding/hooks/useCurrentRoute";
import { useRouteDisplay } from "@/features/pathFinding/hooks/useRouteDisplay";
import Sidebar from "./components/Sidebar";
import LocationButton from "@/components/LocationButton";
import InformationModal from "@/components/InformationModal";
import { calculateMapConfig } from "./utils/mapUtils";
import { MAP_CONFIG } from "@/config/constants";
import { useFloorJumpSpeechBubble } from "@/features/pathFinding/hooks/useFloorJumpSpeechBubble";
import { useStairsFloorPopup } from "@/features/pathFinding/hooks/useStairsFloorPopup";
import { useStartMarkers } from "@/features/pathFinding/hooks/useStartMarkers";
import { LocationInput } from "@/features/pathFinding/types/routingInterface";
import { RouteResultForMapView, RoutingMode } from "@/features/pathFinding/types/routing";
import { useCurrentLocationDisplay } from "@/hooks/useCurrentLocationDisplay";
import { useFacilityNameBubble } from "@/hooks/useFacilityNameBubble";
import { useInactivityTimer } from "@/hooks/useInactivityTimer";
import Shortcut from "@/components/Shortcut";
import { DIGITAL_SIGNAGE_LOCATION } from "@/config/constants";
import { MetadataItem } from "@/types/metadata";
import { useSearchParams } from "@/hooks/useSearchParams";
import {
  trackFacilitySelection,
  trackSessionStart,
  trackSessionHeartbeat,
  trackSessionEnd,
} from "@/utils/analytics";
import LanguageSelector from "@/components/LanguageSelector";
import Text from "@/components/Text";
import { MdSearch } from "react-icons/md";
import DebugDiagnosticsPanel from "@/components/DebugDiagnosticsPanel";
import Toast from "@/components/Toast";
import KioskResetWarning from "@/components/KioskResetWarning";
import { useDiagnosticsTrigger } from "@/hooks/useDiagnosticsTrigger";
import { diagnostics } from "@/lib/diagnostics";

type LngLatTuple = [number, number];

const ROUTE_CAMERA_CONFIG = {
  minExtent: 0.002,
  paddingRatio: 0.25,
  minPadding: 0.0012,
  basePaddingPx: 120,
  basePaddingRatio: 0.085,
  basePaddingMinPx: 800,
  basePaddingMaxPx: 220,
  bottomExtraPx: 48,
  singlePointEaseDuration: 480,
  fitDuration: 480,
  maxZoom: Math.min(17.2, MAP_CONFIG.maxZoom),
} as const;

// anchor:"bottom" バブルマーカーの推定サイズ（マージン込み）
// 経路カメラの計算で、アンカーの真上にこの大きさの矩形が画面上にあるものとして扱う
// 施設名バブルは最も長い英語名で幅 24rem・3 行になるので、それが収まる大きさにしている
const BUBBLE_HEIGHT_WITH_MARGIN_PX = 135; // 本体+三角+bounce+余白
const BUBBLE_HALF_WIDTH_WITH_MARGIN_PX = 200; // 横幅の半分+余白

type CameraState = {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
  padding?: PaddingOptions;
};

type RouteShareParams = {
  goalFacilityId: string;
  mode: RoutingMode;
};
const MOBILE_BREAKPOINT = 640;
const DESKTOP_LOCATION_BUTTON_RESERVED_WIDTH = 196;
const MOBILE_INITIAL_SIDEBAR_RATIO = 0.26;
const MOBILE_INITIAL_SIDEBAR_MIN_HEIGHT_PX = 120;
const MOBILE_TOP_CLEARANCE_PX = 104;
const MOBILE_MIN_ROUTE_ZOOM = 15.5;

const KIOSK_INACTIVITY_TIMEOUT_MS = 120 * 1000;
const KIOSK_INACTIVITY_WARNING_MS = 10 * 1000;
const TOAST_DURATION_MS = 4000;
const KIOSK_MODE = import.meta.env.VITE_KIOSK_MODE === "true";

const getInitialMobileSidebarHeight = (viewportHeight: number) =>
  Math.max(
    MOBILE_INITIAL_SIDEBAR_MIN_HEIGHT_PX,
    Math.round(viewportHeight * MOBILE_INITIAL_SIDEBAR_RATIO),
  );

const getMobileViewportMetrics = () => {
  if (typeof window === "undefined") {
    return {
      height: MOBILE_INITIAL_SIDEBAR_MIN_HEIGHT_PX,
      bottomInset: 0,
    };
  }

  const visualViewport = window.visualViewport;
  const height = Math.round(visualViewport?.height ?? window.innerHeight);
  const bottomInset = visualViewport
    ? Math.max(0, Math.round(window.innerHeight - visualViewport.height - visualViewport.offsetTop))
    : 0;

  return { height, bottomInset };
};

const clampMobileSidebarHeight = (height: number, viewportHeight: number) => {
  const minHeight = getInitialMobileSidebarHeight(viewportHeight);
  const maxHeight = Math.max(minHeight, viewportHeight - MOBILE_TOP_CLEARANCE_PX);
  return Math.max(minHeight, Math.min(height, maxHeight));
};

// 吹き出し等の DOM マーカー経由のタップを除く
const isDirectMapTap = (e: maplibregl.MapLayerMouseEvent): boolean =>
  e.originalEvent.target === e.target.getCanvas();

const toLngLatTuple = (position: Position): LngLatTuple | null => {
  const [lng, lat] = position;
  if (typeof lng !== "number" || typeof lat !== "number") return null;
  return [lng, lat];
};

const extractCoordinatesFromGeometry = (geometry: Geometry): LngLatTuple[] => {
  const positions: LngLatTuple[] = [];
  const processPositions = (coords: Position[]) => {
    coords.forEach((pos) => {
      const tuple = toLngLatTuple(pos);
      if (tuple) positions.push(tuple);
    });
  };

  switch (geometry.type) {
    case "Point":
      processPositions([geometry.coordinates]);
      break;
    case "MultiPoint":
    case "LineString":
      processPositions(geometry.coordinates);
      break;
    case "MultiLineString":
    case "Polygon":
      geometry.coordinates.forEach(processPositions);
      break;
    case "MultiPolygon":
      geometry.coordinates.forEach((poly) => poly.forEach(processPositions));
      break;
    case "GeometryCollection":
      geometry.geometries.forEach((geom) =>
        positions.push(...extractCoordinatesFromGeometry(geom)),
      );
      break;
  }
  return positions;
};

const collectRoutePositionsForFloor = (
  route: RouteResultForMapView,
  floorId: string,
): LngLatTuple[] => {
  const positions: LngLatTuple[] = [];
  route.lineGeometry?.features?.forEach((feature) => {
    if (feature?.properties?.floorId === floorId && feature.geometry) {
      positions.push(...extractCoordinatesFromGeometry(feature.geometry));
    }
  });
  route.nodes.forEach((node) => {
    if (node.floorId === floorId) {
      positions.push([node.position.x, node.position.y]);
    }
  });
  route.floorJumpDatas?.forEach((jump) => {
    if (jump.floorIdGoingFrom === floorId) {
      positions.push([jump.fromNodePosition.x, jump.fromNodePosition.y]);
    }
    if (jump.floorIdGoingTo === floorId) {
      positions.push([jump.toNodePosition.x, jump.toNodePosition.y]);
    }
  });
  const firstNode = route.nodes[0];
  const lastNode = route.nodes[route.nodes.length - 1];
  if (firstNode?.floorId === floorId) {
    positions.push([firstNode.position.x, firstNode.position.y]);
  }
  if (lastNode?.floorId === floorId) {
    positions.push([lastNode.position.x, lastNode.position.y]);
  }

  return Array.from(
    new Map(
      positions.map(([lng, lat]) => [
        `${lng.toFixed(6)}:${lat.toFixed(6)}`,
        [lng, lat] as LngLatTuple,
      ]),
    ).values(),
  );
};

const getBubbleAnchorsForFloor = (route: RouteResultForMapView, floorId: string): LngLatTuple[] => {
  const anchors: LngLatTuple[] = [];
  route.floorJumpDatas?.forEach((jump) => {
    if (jump.floorIdGoingFrom === floorId) {
      anchors.push([jump.fromNodePosition.x, jump.fromNodePosition.y]);
    }
  });
  const firstNode = route.nodes[0];
  if (firstNode?.floorId === floorId) {
    anchors.push([firstNode.position.x, firstNode.position.y]);
  }
  // ゴール施設バブル (FacilityNameBubble) が上部UIと重ならないようゴール座標も含める
  const lastNode = route.nodes[route.nodes.length - 1];
  if (lastNode && lastNode !== firstNode && lastNode.floorId === floorId) {
    anchors.push([lastNode.position.x, lastNode.position.y]);
  }
  return anchors;
};

const CAMERA_FIT_MAX_ITERATIONS = 10;

// maplibre の cameraForBounds / fitBounds はピッチを無視し、真上から見た平面で zoom と中心を決める。
// 鳥瞰ピッチでは手前 (画面下) が拡大されるため、南北に長い経路 (研究棟行きなど) だと
// 出発地が画面下にはみ出す。実際のピッチで投影しながら zoom と中心を詰める。
// 吹き出しは画面上で固定サイズなので、投影後の座標に矩形として足す。
// v6 は transform を公開していないため jumpTo で仮置きして測り、最後に元へ戻す
// (同期処理の間は描画されないので画面には出ない)
const cameraForPositionsWithPitch = (
  mapInstance: maplibregl.Map,
  positions: LngLatTuple[],
  bubbleAnchors: LngLatTuple[],
  padding: PaddingOptions,
  maxZoom: number,
): { center: maplibregl.LngLat; zoom: number } | null => {
  const bounds = positions.reduce(
    (b, pos) => b.extend(pos),
    new maplibregl.LngLatBounds(positions[0], positions[0]),
  );
  const flatCamera = mapInstance.cameraForBounds(bounds, { padding, maxZoom });
  if (!flatCamera?.zoom) return null;

  const canvas = mapInstance.getCanvas();
  const left = padding.left ?? 0;
  const right = canvas.clientWidth - (padding.right ?? 0);
  const top = padding.top ?? 0;
  const bottom = canvas.clientHeight - (padding.bottom ?? 0);
  if (right <= left || bottom <= top) return null;

  const minZoom = mapInstance.getMinZoom();
  // 寄りすぎた位置から始めると手前の点がカメラの後ろに回り込むので、平面計算より引いて始める
  let zoom = Math.max(minZoom, flatCamera.zoom - 1);
  let center = bounds.getCenter();

  const saved = {
    center: mapInstance.getCenter(),
    zoom: mapInstance.getZoom(),
    padding: mapInstance.getPadding(),
  };
  try {
    for (let i = 0; i < CAMERA_FIT_MAX_ITERATIONS; i++) {
      mapInstance.jumpTo({ center, zoom, padding });

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      const include = (x: number, y: number) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      };
      positions.forEach((pos) => {
        const p = mapInstance.project(pos);
        include(p.x, p.y);
      });
      bubbleAnchors.forEach((pos) => {
        const p = mapInstance.project(pos);
        include(p.x - BUBBLE_HALF_WIDTH_WITH_MARGIN_PX, p.y - BUBBLE_HEIGHT_WITH_MARGIN_PX);
        include(p.x + BUBBLE_HALF_WIDTH_WITH_MARGIN_PX, p.y);
      });

      const scale = Math.min(
        (right - left) / Math.max(maxX - minX, 1),
        (bottom - top) / Math.max(maxY - minY, 1),
      );
      const offset = Math.hypot(
        (minX + maxX) / 2 - (left + right) / 2,
        (minY + maxY) / 2 - (top + bottom) / 2,
      );
      // 中心はパディング領域の中央に投影されるので、外接矩形の中央にある地点を次の中心にする
      center = mapInstance.unproject([(minX + maxX) / 2, (minY + maxY) / 2]);
      const nextZoom = Math.min(maxZoom, Math.max(minZoom, zoom + Math.log2(scale)));
      const converged = Math.abs(nextZoom - zoom) < 0.01 && offset < 2;
      zoom = nextZoom;
      if (converged) break;
    }
  } finally {
    mapInstance.jumpTo(saved);
  }

  return { center, zoom };
};

export default function App() {
  const cameraStateStackRef = useRef<CameraState[]>([]);
  const latestPolygonsRef = useRef<FeatureCollection<Geometry> | null>(null);
  const latestIconsRef = useRef<FeatureCollection<Geometry> | null>(null);
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
  const rotationLockHandlerRef = useRef<
    ((event: maplibregl.MapEventType["rotate"]) => void) | null
  >(null);
  const [currentRoutingMode, setCurrentRoutingMode] = useState<RoutingMode>("default");
  const [hasAlternativeRoute, setHasAlternativeRoute] = useState<boolean>(false);
  const [activeRouteShareParams, setActiveRouteShareParams] = useState<RouteShareParams | null>(
    null,
  );
  const [sharedRouteRequest, setSharedRouteRequest] = useState<RouteShareParams | null>(null);
  const [shareBaseUrl, setShareBaseUrl] = useState<string | null>(null);
  const [isShareUrlLoading, setIsShareUrlLoading] = useState(false);
  const [pendingFloorChange, setPendingFloorChangeState] = useState<string | null>(null);
  const { i18n, t } = useTranslation();
  const language = useMemo<"ja" | "en">(() => {
    const resolved = i18n.resolvedLanguage || i18n.language;
    return resolved?.startsWith("en") ? "en" : "ja";
  }, [i18n.language, i18n.resolvedLanguage]);
  const lastAppliedLanguageRef = useRef<"ja" | "en">(language);
  const {
    map,
    isMapLoaded,
    initializeMap,
    updateMapData,
    updateBuildingPointers,
    updateBuildingPointerLanguage,
  } = useMapInitialization();
  const { currentFloorId, availableFloors, setFloor, getFloorGeoJsonPath } = useFloorState();
  const [isRouteActive, setIsRouteActive] = useState(false);
  const { currentRoute, searchRoute, clearRoute, prepareRouting } = useCurrentRoute();
  const { displayRoute, clearRouteDisplay, updateRouteLayerForFloor, bringRouteLayersToFront } =
    useRouteDisplay(map);
  const searchParams = useSearchParams();
  const isInitialized = useRef<boolean>(false);
  // 初回マップロードの renderMs 計測用。フロア切替経路を通らないため別途計測する。
  const initRenderStartRef = useRef<number | null>(null);
  const initRenderRecordedRef = useRef<boolean>(false);

  useEffect(() => {
    const goal = searchParams.get("goal");
    const modeParam = searchParams.get("mode");
    if (!goal) {
      setSharedRouteRequest(null);
      return;
    }
    const normalizedMode: RoutingMode = modeParam === "barrier-free" ? "barrier-free" : "default";
    setSharedRouteRequest({
      goalFacilityId: goal,
      mode: normalizedMode,
    });
  }, [searchParams]);

  useEffect(() => {
    const baseUrl =
      import.meta.env.VITE_SHARE_BASE_URL ?? `${window.location.origin}${window.location.pathname}`;
    setShareBaseUrl(baseUrl);
  }, []);

  const shareUrl = useMemo(() => {
    if (!activeRouteShareParams || !shareBaseUrl) {
      return null;
    }
    const params = new URLSearchParams({
      goal: activeRouteShareParams.goalFacilityId,
      mode: activeRouteShareParams.mode,
    });
    return `${shareBaseUrl}?${params.toString()}`;
  }, [activeRouteShareParams, shareBaseUrl]);

  useEffect(() => {
    if (!activeRouteShareParams) {
      setIsShareUrlLoading(false);
      return;
    }
    setIsShareUrlLoading(!shareUrl);
  }, [activeRouteShareParams, shareUrl]);

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileDestButton, setShowMobileDestButton] = useState(false);
  const mobileDestClickRef = useRef<(() => void) | null>(null);

  const [kioskResetKey, setKioskResetKey] = useState(0);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((message: string) => {
    setToastMessage(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMessage(null), TOAST_DURATION_MS);
  }, []);
  useEffect(
    () => () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    },
    [],
  );
  // 経路表示中は施設を選択できないので、タップされたら理由を案内する
  const showRouteActiveToast = useCallback(() => {
    showToast(
      isMobile
        ? t("経路を表示中です。ほかの場所を調べるには「戻る」を押してください。")
        : t("経路を表示中です。ほかの場所を調べるには「最初の画面に戻る」を押してください。"),
    );
  }, [isMobile, showToast, t]);

  const {
    displayFloorJumpSpeechBubble,
    clearFloorJumpSpeechBubble,
    updateFloorJumpSpeechBubbleForFloor,
  } = useFloorJumpSpeechBubble(map);

  const { displayStartMarkers, clearStartMarkers, updateStartMarkersForFloor } = useStartMarkers(
    map,
    isMobile,
  );

  const {
    initializeCurrentLocation,
    updateCurrentLocationMarkerForFloor,
    replayCurrentLocationPulse,
  } = useCurrentLocationDisplay(map, currentFloorId, isMobile, isRouteActive);

  const { displayFacilityNameBubble, clearFacilityNameBubble } = useFacilityNameBubble(map);

  const [highlightedFacility, setHighlightedFacility] = useState<{
    id: string;
    name: string;
    floorId: string;
  } | null>(null);
  const getLocalizedFacilityNameFromCollection = useCallback(
    (facilityId: string, collection?: FeatureCollection<Geometry> | null): string | null => {
      const polygons = collection ?? latestPolygonsRef.current;
      if (!facilityId || !polygons) return null;

      const targetFeature = polygons.features.find(
        (feature) => feature?.properties?.id === facilityId,
      );
      if (!targetFeature) return null;

      const properties = targetFeature.properties as GeoJsonProperties | undefined;
      if (!properties) return null;

      const roomName =
        typeof properties.room === "string"
          ? properties.room
          : typeof properties.name === "string"
            ? properties.name
            : null;

      return roomName ?? null;
    },
    [],
  );

  const pendingFloorChangeRef = useRef<string | null>(null);
  const floorChangeInProgressRef = useRef<string | null>(null);
  const lastCompletedFloorRef = useRef<string>(currentFloorId);
  const sessionIdRef = useRef<string | null>(null);
  const sessionStartTimeRef = useRef<number | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionEndedRef = useRef(false);
  const diagnosticsTrigger = useDiagnosticsTrigger();

  useEffect(() => {
    lastCompletedFloorRef.current = currentFloorId;
  }, [currentFloorId]);

  // マップロード完了後、現在フロア以外を低優先でバックグラウンドプリフェッチ
  useEffect(() => {
    if (!isMapLoaded) return;
    const otherFloors = availableFloors.filter((f) => f.id !== currentFloorId);
    prefetchFloors(otherFloors, language);

    // 経路グラフも空き時間に組み立てておく (毎朝の再起動後、最初の経路探索だけ遅くなるのを防ぐ)
    const warmUpRouting = () => {
      prepareRouting().catch((error) => console.warn("経路データの事前準備に失敗:", error));
    };
    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(warmUpRouting, { timeout: 5000 });
      return () => window.cancelIdleCallback(idleId);
    }
    const timerId = setTimeout(warmUpRouting, 1000);
    return () => clearTimeout(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapLoaded]); // 初回マップロード時のみ実行

  // 初回マップロードの renderMs を記録 (データ取得開始〜描画完了)。一度だけ。
  useEffect(() => {
    if (!isMapLoaded || initRenderRecordedRef.current) return;
    initRenderRecordedRef.current = true;
    if (initRenderStartRef.current !== null) {
      diagnostics.recordMetric("renderMs", performance.now() - initRenderStartRef.current);
    }
  }, [isMapLoaded]);

  useEffect(() => {
    const createSessionId = () => {
      if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
      }
      return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    };

    const startTimestamp = Date.now();
    const sessionId = createSessionId();
    sessionIdRef.current = sessionId;
    sessionStartTimeRef.current = startTimestamp;
    const startedAtIso = new Date(startTimestamp).toISOString();
    trackSessionStart({
      sessionId,
      startedAt: startedAtIso,
    });
    diagnostics.recordEvent("session_started", sessionId.slice(0, 8));

    const HEARTBEAT_INTERVAL_MS = 60_000;
    heartbeatTimerRef.current = setInterval(() => {
      if (!sessionStartTimeRef.current || !sessionIdRef.current) {
        return;
      }
      const elapsedSeconds = Math.round((Date.now() - sessionStartTimeRef.current) / 1000);
      trackSessionHeartbeat({
        sessionId: sessionIdRef.current,
        elapsedSeconds,
      });
    }, HEARTBEAT_INTERVAL_MS);

    const endSession = () => {
      if (sessionEndedRef.current) return;
      if (!sessionIdRef.current || !sessionStartTimeRef.current) return;
      const durationSeconds = Math.max(
        1,
        Math.round((Date.now() - sessionStartTimeRef.current) / 1000),
      );
      trackSessionEnd({
        sessionId: sessionIdRef.current,
        durationSeconds,
      });
      sessionEndedRef.current = true;
    };

    const handleBeforeUnload = () => {
      endSession();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
      }
      endSession();
    };
  }, []);

  const collectFacilityPositions = (facilityId: string): LngLatTuple[] => {
    const positions: LngLatTuple[] = [];
    const appendFromCollection = (collection: FeatureCollection<Geometry> | null) => {
      collection?.features.forEach((feature) => {
        if (feature?.properties?.id === facilityId && feature.geometry) {
          positions.push(...extractCoordinatesFromGeometry(feature.geometry));
        }
      });
    };
    appendFromCollection(latestIconsRef.current);
    appendFromCollection(latestPolygonsRef.current);
    return Array.from(
      new Map(
        positions.map(([lng, lat]) => [
          `${lng.toFixed(6)}:${lat.toFixed(6)}`,
          [lng, lat] as LngLatTuple,
        ]),
      ).values(),
    );
  };

  const getFeatureCenter = useCallback(
    (feature: Feature<Geometry, GeoJsonProperties>): LngLatTuple | null => {
      if (!feature.geometry) {
        return null;
      }
      const coordinates = extractCoordinatesFromGeometry(feature.geometry);
      if (coordinates.length === 0) {
        return null;
      }
      const bounds = coordinates.reduce(
        (acc, coordinate) => acc.extend(coordinate),
        new maplibregl.LngLatBounds(coordinates[0], coordinates[0]),
      );
      const center = bounds.getCenter();
      return [center.lng, center.lat];
    },
    [],
  );

  const updatePendingFloorChange = useCallback(
    (nextFloorId: string | null) => {
      pendingFloorChangeRef.current = nextFloorId;
      setPendingFloorChangeState(nextFloorId);
    },
    [setPendingFloorChangeState],
  );

  const computeCameraPadding = useCallback((): PaddingOptions => {
    if (isMobile) {
      return {
        top: MOBILE_TOP_CLEARANCE_PX + BUBBLE_HEIGHT_WITH_MARGIN_PX,
        bottom: sidebarHeightRef.current + 24,
        left: 24,
        right: 24,
      };
    }
    const containerHeight = mapContainer.current?.clientHeight ?? window.innerHeight ?? 720;
    const basePadding = Math.min(
      Math.max(
        containerHeight * ROUTE_CAMERA_CONFIG.basePaddingRatio,
        ROUTE_CAMERA_CONFIG.basePaddingMinPx,
      ),
      ROUTE_CAMERA_CONFIG.basePaddingMaxPx,
    );
    return {
      top: basePadding,
      bottom: basePadding + ROUTE_CAMERA_CONFIG.bottomExtraPx,
      left: basePadding,
      right: basePadding,
    };
  }, [isMobile, mapContainer]);

  const focusOnCoordinates = useCallback(
    (
      positions: LngLatTuple[],
      duration: number = ROUTE_CAMERA_CONFIG.fitDuration,
      centerFallback?: LngLatTuple,
      bubbleAnchors: LngLatTuple[] = [],
    ) => {
      const mapInstance = map.current;
      if (!mapInstance || positions.length === 0) {
        return;
      }

      const padding = computeCameraPadding();
      const maxZoom = Math.min(
        ROUTE_CAMERA_CONFIG.maxZoom,
        typeof mapInstance.getMaxZoom === "function"
          ? mapInstance.getMaxZoom()
          : ROUTE_CAMERA_CONFIG.maxZoom,
      );

      if (positions.length === 1) {
        mapInstance.easeTo({
          center: positions[0],
          zoom: maxZoom,
          duration,
          padding,
          essential: true,
        });
        return;
      }

      const bounds = positions.reduce(
        (b, pos) => b.extend(pos),
        new maplibregl.LngLatBounds(positions[0], positions[0]),
      );

      // モバイルで経路が長すぎてセーフエリアに収まらない場合は正面玄関を中央に固定する
      if (isMobile && centerFallback) {
        const predicted = mapInstance.cameraForBounds(bounds, { padding, maxZoom });
        if (predicted?.zoom != null && predicted.zoom < MOBILE_MIN_ROUTE_ZOOM) {
          mapInstance.easeTo({
            center: centerFallback,
            zoom: maxZoom,
            duration,
            padding,
            essential: true,
          });
          return;
        }
      }

      if (!isMobile) {
        const camera = cameraForPositionsWithPitch(
          mapInstance,
          positions,
          bubbleAnchors,
          padding,
          maxZoom,
        );
        if (camera) {
          mapInstance.easeTo({ ...camera, padding, duration, essential: true });
          return;
        }
      }

      mapInstance.fitBounds(bounds, {
        padding,
        duration,
        maxZoom,
        essential: true,
      });
    },
    [computeCameraPadding, isMobile, map],
  );
  const handleFocusOnFacility = (facilityId: string, options?: { skipCameraSave?: boolean }) => {
    const mapInstance = map.current;
    if (!mapInstance) return;

    let positions = collectFacilityPositions(facilityId);

    if (positions.length === 0 && mapInstance.isStyleLoaded()) {
      const queriedFeatures = [
        ...mapInstance.querySourceFeatures("floor-icons", {
          filter: ["==", ["get", "id"], facilityId],
        }),
        ...mapInstance.querySourceFeatures("floor-polygons", {
          filter: ["==", ["get", "id"], facilityId],
        }),
      ];
      queriedFeatures.forEach((feature) => {
        if (feature.geometry) {
          positions.push(...extractCoordinatesFromGeometry(feature.geometry as Geometry));
        }
      });
      positions = Array.from(
        new Map(
          positions.map(([lng, lat]) => [
            `${lng.toFixed(6)}:${lat.toFixed(6)}`,
            [lng, lat] as LngLatTuple,
          ]),
        ).values(),
      );
    }

    if (positions.length === 0) return;

    if (!options?.skipCameraSave) {
      saveCameraState();
    }

    focusOnCoordinates(positions, ROUTE_CAMERA_CONFIG.singlePointEaseDuration);
  };
  const handleFloorChange = useCallback(
    async (floorId: string) => {
      if (!floorId) return;

      if (!isMapLoaded || !map.current) {
        if (pendingFloorChangeRef.current !== floorId) {
          updatePendingFloorChange(floorId);
        }
        return;
      }

      if (floorChangeInProgressRef.current) {
        if (floorChangeInProgressRef.current === floorId) {
          return;
        }
        if (pendingFloorChangeRef.current !== floorId) {
          updatePendingFloorChange(floorId);
        }
        return;
      }

      try {
        floorChangeInProgressRef.current = floorId;
        updatePendingFloorChange(null);
        setFloor(floorId);
        const floor = availableFloors.find((f) => f.id === floorId);
        if (floor) {
          const renderStart = performance.now();
          const { scaledPolygons, scaledIconsGeoJSON, doorGapFeatures } = await fetchScaledMapData(
            floor.path,
            language,
          );

          if (scaledPolygons && scaledIconsGeoJSON && doorGapFeatures) {
            latestPolygonsRef.current = scaledPolygons;
            latestIconsRef.current = scaledIconsGeoJSON;
            updateMapData(scaledPolygons, scaledIconsGeoJSON, doorGapFeatures);
          }
          updateRouteLayerForFloor(floorId);
          updateFloorJumpSpeechBubbleForFloor(floorId);
          updateStartMarkersForFloor(floorId);
          updateCurrentLocationMarkerForFloor(floorId);
          updateBuildingPointers(floorId);
          lastCompletedFloorRef.current = floorId;
          diagnostics.recordEvent("floor_changed", floorId);
          diagnostics.recordMetric("renderMs", performance.now() - renderStart);

          if (highlightedFacility?.floorId === floorId) {
            const facilityToRestore = { ...highlightedFacility };
            setTimeout(() => {
              setHighlightedFacility((current) => {
                if (!current) {
                  return current;
                }
                if (current.id !== facilityToRestore.id) {
                  return current;
                }
                return facilityToRestore;
              });
            }, 100);
          }

          if (currentRoute.current) {
            const positions = collectRoutePositionsForFloor(currentRoute.current, floorId);
            if (isMobile) {
              const fallback: LngLatTuple | undefined =
                positions.length > 0 ? positions[0] : undefined;
              focusOnCoordinates(positions, ROUTE_CAMERA_CONFIG.fitDuration, fallback);
            } else {
              const bubbleAnchors = getBubbleAnchorsForFloor(currentRoute.current, floorId);
              focusOnCoordinates(
                positions,
                ROUTE_CAMERA_CONFIG.fitDuration,
                undefined,
                bubbleAnchors,
              );
            }
          }
        }
      } catch (error) {
        diagnostics.recordError(error, `floor_change_failed:${floorId}`);
        console.error(`フロア${floorId}のデータ読み込みに失敗:`, error);
      } finally {
        floorChangeInProgressRef.current = null;
        const queuedFloorId = pendingFloorChangeRef.current;
        if (queuedFloorId && queuedFloorId !== floorId) {
          handleFloorChange(queuedFloorId);
        }
      }
    },
    [
      availableFloors,
      currentRoute,
      focusOnCoordinates,
      highlightedFacility,
      isMobile,
      isMapLoaded,
      language,
      map,
      setFloor,
      setHighlightedFacility,
      updateBuildingPointers,
      updateCurrentLocationMarkerForFloor,
      updateFloorJumpSpeechBubbleForFloor,
      updateMapData,
      updateRouteLayerForFloor,
      updateStartMarkersForFloor,
      updatePendingFloorChange,
    ],
  );

  // フロアジャンプ吹き出しや階段ポップアップはクリックハンドラを生成時に保持する。
  // 生成時の handleFloorChange だと言語・経路表示状態などが古いまま階層移動するため、常に最新を呼ぶ
  const handleFloorChangeRef = useRef(handleFloorChange);
  useEffect(() => {
    handleFloorChangeRef.current = handleFloorChange;
  }, [handleFloorChange]);
  const changeFloorWithLatestHandler = useCallback(
    (floorId: string) => handleFloorChangeRef.current(floorId),
    [],
  );

  const { showStairsPopup, hideStairsPopup } = useStairsFloorPopup(
    map,
    changeFloorWithLatestHandler,
    language,
  );

  useEffect(() => {
    hideStairsPopup();
  }, [currentFloorId, hideStairsPopup]);

  const saveCameraState = () => {
    const mapInstance = map.current;
    if (!mapInstance) return;
    const currentState: CameraState = {
      center: mapInstance.getCenter().toArray() as [number, number],
      zoom: mapInstance.getZoom(),
      bearing: mapInstance.getBearing(),
      pitch: mapInstance.getPitch(),
      padding: mapInstance.getPadding?.(),
    };
    const lastState = cameraStateStackRef.current[cameraStateStackRef.current.length - 1];
    if (
      lastState &&
      lastState.zoom === currentState.zoom &&
      lastState.bearing === currentState.bearing &&
      lastState.pitch === currentState.pitch &&
      lastState.center[0] === currentState.center[0] &&
      lastState.center[1] === currentState.center[1]
    ) {
      return;
    }
    cameraStateStackRef.current.push(currentState);
  };

  const restoreCameraState = () => {
    const mapInstance = map.current;
    if (!mapInstance) return;
    const previousState = cameraStateStackRef.current.pop();
    if (!previousState) return;
    mapInstance.easeTo({
      ...previousState,
      duration: ROUTE_CAMERA_CONFIG.singlePointEaseDuration,
      essential: true,
    });
  };

  const handleRouteSearch = async (
    start: LocationInput,
    goal: LocationInput,
    mode: RoutingMode,
  ): Promise<RouteResultForMapView | null> => {
    hideStairsPopup();
    const goalLabel = typeof goal === "string" ? goal : `${goal.floorId}@coords`;
    diagnostics.recordEvent("route_search_started", `${goalLabel}/${mode}`);
    const hadRoute = Boolean(currentRoute.current);
    const newRoute = await diagnostics.measure("routeMs", () => searchRoute(start, goal, mode));

    if (newRoute) {
      setIsRouteActive(true);
      if (!hadRoute) {
        saveCameraState();
      }
      setCurrentRoutingMode(mode);
      displayRoute(currentFloorId, newRoute);
      displayFloorJumpSpeechBubble(currentFloorId, newRoute, changeFloorWithLatestHandler);
      displayStartMarkers(currentFloorId, newRoute);

      const positions = collectRoutePositionsForFloor(newRoute, currentFloorId);
      if (isMobile) {
        const startNode = newRoute.nodes[0];
        const fallback: LngLatTuple | undefined = startNode
          ? [startNode.position.x, startNode.position.y]
          : undefined;
        focusOnCoordinates(positions, ROUTE_CAMERA_CONFIG.fitDuration, fallback);
      } else {
        const bubbleAnchors = getBubbleAnchorsForFloor(newRoute, currentFloorId);
        focusOnCoordinates(positions, ROUTE_CAMERA_CONFIG.fitDuration, undefined, bubbleAnchors);
      }

      if (mode === "default") {
        const hasStairs = newRoute.edges?.some((edge) => edge.linkType === "stairs") ?? false;
        setHasAlternativeRoute(hasStairs);
      } else {
        setHasAlternativeRoute(true);
      }
      diagnostics.recordEvent("route_search_completed", goalLabel);
      // 座標目的地は facilityId を持たないので null を渡す (日次集計対象外)。
      diagnostics.recordSearch(typeof goal === "string" ? goal : null);
    } else {
      showToast(t("経路が見つかりませんでした"));
      setHasAlternativeRoute(false);
      diagnostics.recordEvent("route_search_no_result", `${goalLabel}/${mode}`);
    }
    return newRoute ?? null;
  };

  const handleRouteClear = () => {
    hideStairsPopup();
    clearRoute();
    clearRouteDisplay();
    clearFloorJumpSpeechBubble();
    clearStartMarkers();
    setHasAlternativeRoute(false);
    setActiveRouteShareParams(null);
    setIsShareUrlLoading(false);
    setIsRouteActive(false);
    diagnostics.recordEvent("route_cleared");
  };

  const wasMobileViewportRef = useRef<boolean>(false);

  const [sidebarHeight, setSidebarHeight] = useState(() =>
    typeof window === "undefined"
      ? MOBILE_INITIAL_SIDEBAR_MIN_HEIGHT_PX
      : getInitialMobileSidebarHeight(getMobileViewportMetrics().height),
  );
  const sidebarHeightRef = useRef(sidebarHeight);
  useEffect(() => {
    sidebarHeightRef.current = sidebarHeight;
  }, [sidebarHeight]);

  const clearRotationLock = useCallback((mapInstance: maplibregl.Map) => {
    if (rotationLockHandlerRef.current) {
      mapInstance.off("rotate", rotationLockHandlerRef.current);
      rotationLockHandlerRef.current = null;
    }
  }, []);

  const applyRotationPolicy = useCallback(
    (mapInstance: maplibregl.Map, allowRotation: boolean) => {
      clearRotationLock(mapInstance);

      if (allowRotation) {
        mapInstance.dragRotate?.enable?.();
        mapInstance.touchZoomRotate?.enableRotation?.();
        mapInstance.keyboard?.enableRotation?.();
        return;
      }

      const lockBearingToNorth = () => {
        if (mapInstance.getBearing() !== 0) {
          mapInstance.setBearing(0);
        }
      };

      rotationLockHandlerRef.current = lockBearingToNorth;

      mapInstance.dragRotate?.enable?.();
      mapInstance.keyboard?.enable?.();
      mapInstance.setBearing(0);
      mapInstance.on("rotate", lockBearingToNorth);
    },
    [clearRotationLock],
  );

  useEffect(() => {
    const evaluateViewport = () => {
      const width = window.innerWidth;
      const { height } = getMobileViewportMetrics();
      const shortestSide = Math.min(width, height);
      const isMobileView = shortestSide < MOBILE_BREAKPOINT;

      setIsMobile(isMobileView);

      if (isMobileView) {
        setSidebarHeight((currentHeight) =>
          wasMobileViewportRef.current
            ? clampMobileSidebarHeight(currentHeight, height)
            : getInitialMobileSidebarHeight(height),
        );
      }

      wasMobileViewportRef.current = isMobileView;
    };

    evaluateViewport();
    window.addEventListener("resize", evaluateViewport);
    window.addEventListener("orientationchange", evaluateViewport);
    window.visualViewport?.addEventListener("resize", evaluateViewport);
    window.visualViewport?.addEventListener("scroll", evaluateViewport);

    return () => {
      window.removeEventListener("resize", evaluateViewport);
      window.removeEventListener("orientationchange", evaluateViewport);
      window.visualViewport?.removeEventListener("resize", evaluateViewport);
      window.visualViewport?.removeEventListener("scroll", evaluateViewport);
    };
  }, []);

  useEffect(() => {
    const mapInstance = map.current;
    if (!mapInstance || !isMapLoaded) return;

    applyRotationPolicy(mapInstance, isMobile);
    return () => {
      clearRotationLock(mapInstance);
    };
  }, [applyRotationPolicy, clearRotationLock, isMapLoaded, isMobile, map]);

  const handleResetMap = async () => {
    if (map.current) {
      if (currentFloorId !== "main-3") {
        await handleFloorChange("main-3");
      }
      const { scaledCenter } = calculateMapConfig();
      const defaultZoom = MAP_CONFIG.initialZoom ?? MAP_CONFIG.minZoom + 1;
      const zoom =
        isMobile && MAP_CONFIG.mobileMinZoom
          ? Math.max(MAP_CONFIG.mobileMinZoom, defaultZoom)
          : defaultZoom;
      map.current.flyTo({
        center: scaledCenter,
        zoom,
        bearing: 0,
        pitch: isMobile ? 0 : (MAP_CONFIG.birdseyePitch ?? 0),
        essential: true,
      });
      replayCurrentLocationPulse();
    }
  };

  const handleKioskReset = () => {
    if (!isMapLoaded) return;
    setIsInfoModalOpen(false);
    clearFacilityNameBubble();
    cameraStateStackRef.current = [];
    setKioskResetKey((k) => k + 1);
    diagnostics.recordEvent("kiosk_reset");
  };

  // 保守パネルのQR読取中に予告が重ならないよう、パネル表示中は止める
  const { secondsLeft: kioskResetSecondsLeft, dismissWarning: dismissKioskResetWarning } =
    useInactivityTimer(
      handleKioskReset,
      KIOSK_INACTIVITY_TIMEOUT_MS,
      KIOSK_MODE && !diagnosticsTrigger.isOpen,
      KIOSK_INACTIVITY_WARNING_MS,
    );

  const handleShortcutSelect = (facilityId: string) => {
    if (currentRoute.current) {
      return;
    }
    diagnostics.recordEvent("tap", `shortcut:${facilityId}`);
    setSelectedFacilityId(facilityId);
    setTimeout(() => setSelectedFacilityId(null), 100);
  };

  useEffect(() => {
    if (!map.current) return;

    const facilityFillLayerId = "rooms-fill";

    const handleFacilityFillLayerClick = (e: maplibregl.MapLayerMouseEvent) => {
      if (currentRoute.current) {
        const tappedId = e.features?.[0]?.properties?.id;
        if (isDirectMapTap(e) && typeof tappedId === "string" && tappedId !== "base") {
          showRouteActiveToast();
        }
        return;
      }
      if (e.features && e.features.length > 0) {
        const feature = e.features?.[0] as Feature<Geometry, GeoJsonProperties> | undefined;
        if (!feature) {
          hideStairsPopup();
          return;
        }
        if (feature?.properties && feature.properties.id === "base") {
          return;
        }
        const rawFacilityId = feature.properties?.id;
        const category = feature.properties?.category;
        const isElevatorFeature =
          category === "elevator" ||
          (typeof rawFacilityId === "string" && rawFacilityId.includes("elevator"));
        const isStairsFeature =
          category === "stairs" ||
          (typeof rawFacilityId === "string" && rawFacilityId.includes("stairs"));

        if ((isElevatorFeature || isStairsFeature) && typeof rawFacilityId === "string") {
          const coords = getFeatureCenter(feature) ?? ([e.lngLat.lng, e.lngLat.lat] as LngLatTuple);
          showStairsPopup({
            facilityId: rawFacilityId,
            coords,
            linkType: isElevatorFeature ? "elevator" : "stairs",
          });
          return;
        }

        hideStairsPopup();

        const facilityId = feature.properties?.id;
        if (typeof facilityId !== "string") {
          return;
        }

        const facilityName =
          typeof feature.properties?.room === "string"
            ? feature.properties.room
            : typeof feature.properties?.name === "string"
              ? feature.properties.name
              : undefined;

        trackFacilitySelection({
          facilityId,
          facilityName,
          selectionSource: "map",
        });

        setSelectedFacilityId(facilityId);
        setTimeout(() => setSelectedFacilityId(null), 100);
      }
    };

    map.current.on("click", facilityFillLayerId, handleFacilityFillLayerClick);

    const mapCopyForCleanUp = map.current;

    return () => {
      if (!mapCopyForCleanUp) return;
      mapCopyForCleanUp.off("click", facilityFillLayerId, handleFacilityFillLayerClick);
    };
  }, [
    currentRoute,
    getFeatureCenter,
    hideStairsPopup,
    isMapLoaded,
    map,
    showRouteActiveToast,
    showStairsPopup,
    setSelectedFacilityId,
  ]);

  useEffect(() => {
    if (!map.current) return;

    const facilityLabelLayerId = "rooms-labels";

    const handleStarShapedFacilityClick = (e: maplibregl.MapLayerMouseEvent) => {
      if (currentRoute.current) {
        if (isDirectMapTap(e) && e.features?.[0]?.properties?.shapeType === "STAR") {
          showRouteActiveToast();
        }
        return;
      }
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];

        if (feature.properties.shapeType !== "STAR") {
          return;
        }

        const facilityId = feature.properties.id;
        const facilityName =
          typeof feature.properties.room === "string"
            ? feature.properties.room
            : typeof feature.properties.name === "string"
              ? feature.properties.name
              : undefined;

        trackFacilitySelection({
          facilityId,
          facilityName,
          selectionSource: "map",
        });

        setSelectedFacilityId(facilityId);
        setTimeout(() => setSelectedFacilityId(null), 100);
      }
    };

    map.current.on("click", facilityLabelLayerId, handleStarShapedFacilityClick);

    const mapCopyForCleanUp = map.current;

    return () => {
      if (!mapCopyForCleanUp) return;
      mapCopyForCleanUp.off("click", facilityLabelLayerId, handleStarShapedFacilityClick);
    };
  }, [currentRoute, isMapLoaded, map, showRouteActiveToast]);

  useEffect(() => {
    if (!map.current || !isMapLoaded) return;

    const vendingLayerId = "other-icons";

    const handleVendingIconClick = (e: maplibregl.MapLayerMouseEvent) => {
      if (currentRoute.current) {
        if (isDirectMapTap(e) && e.features?.some((f) => f.properties?.category === "vending")) {
          showRouteActiveToast();
        }
        return;
      }
      if (!e.features || e.features.length === 0) return;

      const feature = e.features.find((f) => f.properties?.category === "vending");
      if (!feature) return;

      const facilityId =
        typeof feature.properties?.pair_facilityId === "string"
          ? feature.properties.pair_facilityId
          : typeof feature.properties?.id === "string"
            ? feature.properties.id
            : null;

      if (!facilityId) return;

      const facilityName =
        typeof feature.properties?.room === "string"
          ? feature.properties.room
          : typeof feature.properties?.name === "string"
            ? feature.properties.name
            : undefined;

      trackFacilitySelection({
        facilityId,
        facilityName,
        selectionSource: "map",
      });

      setSelectedFacilityId(facilityId);
      setTimeout(() => setSelectedFacilityId(null), 100);
    };

    map.current.on("click", vendingLayerId, handleVendingIconClick);

    const mapCopyForCleanUp = map.current;

    return () => {
      if (!mapCopyForCleanUp) return;
      mapCopyForCleanUp.off("click", vendingLayerId, handleVendingIconClick);
    };
  }, [currentRoute, isMapLoaded, map, showRouteActiveToast]);

  const handleRestoreCameraPosition = () => {
    setHighlightedFacility(null);
    clearFacilityNameBubble();

    handleRouteClear();

    restoreCameraState();
  };

  const handleHighlightFacility = (facilityId: string, facilityName: string, floorId: string) => {
    setHighlightedFacility({ id: facilityId, name: facilityName, floorId });
  };

  const handleClearHighlight = () => {
    setHighlightedFacility(null);
    clearFacilityNameBubble();
  };

  const createDigitalSignageStartLocation = (): LocationInput | null => {
    const startLocationInfo = DIGITAL_SIGNAGE_LOCATION;
    if (!startLocationInfo) {
      alert("DIGITAL_SIGNAGE_LOCATIONが見つかりません。");
      return null;
    }
    return {
      position: {
        x: startLocationInfo.position.x,
        y: startLocationInfo.position.y,
      },
      floorId: startLocationInfo.floorId,
    };
  };

  const handleSignageRouteSearch = async (goalItem: MetadataItem | string, mode: RoutingMode) => {
    const start = createDigitalSignageStartLocation();
    if (!start) return;
    setIsShareUrlLoading(true);
    const goal = typeof goalItem === "string" ? goalItem : goalItem.id;
    try {
      const route = await handleRouteSearch(start, goal, mode);
      if (route) {
        setActiveRouteShareParams({
          goalFacilityId: goal,
          mode,
        });
      } else {
        setActiveRouteShareParams(null);
        setIsShareUrlLoading(false);
      }
    } catch (error) {
      setActiveRouteShareParams(null);
      setIsShareUrlLoading(false);
      throw error;
    }
  };

  useEffect(() => {
    if (isInitialized.current) return;
    const initialize = async () => {
      try {
        initRenderStartRef.current = performance.now();
        const data = await fetchScaledMapData(getFloorGeoJsonPath, language);
        if (
          mapContainer.current &&
          data?.scaledPolygons &&
          data?.scaledIconsGeoJSON &&
          data?.doorGapFeatures
        ) {
          latestPolygonsRef.current = data.scaledPolygons;
          latestIconsRef.current = data.scaledIconsGeoJSON;
          initializeMap(
            mapContainer.current,
            data.scaledPolygons,
            data.scaledIconsGeoJSON,
            data.doorGapFeatures,
            language,
          );
        }
      } catch (error) {
        console.error("マップの初期化に失敗しました:", error);
      }
    };
    initialize();
    isInitialized.current = true;
  }, [getFloorGeoJsonPath, initializeMap, language]);

  // AppErrorBoundary の自動復帰で再マウントされるため、地図を破棄しておく
  useEffect(() => {
    const mapRef = map;
    return () => {
      try {
        mapRef.current?.remove();
      } catch {
        // noop
      }
      mapRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    if (!isMapLoaded) return;
    if (lastAppliedLanguageRef.current === language) return;

    const refreshMapLabels = async () => {
      const previousLanguage = lastAppliedLanguageRef.current;
      lastAppliedLanguageRef.current = language;
      try {
        const data = await fetchScaledMapData(getFloorGeoJsonPath, language);
        if (data?.scaledPolygons && data?.scaledIconsGeoJSON && data?.doorGapFeatures) {
          if (lastAppliedLanguageRef.current !== language) {
            return;
          }
          latestPolygonsRef.current = data.scaledPolygons;
          latestIconsRef.current = data.scaledIconsGeoJSON;
          updateMapData(data.scaledPolygons, data.scaledIconsGeoJSON, data.doorGapFeatures);
          if (highlightedFacility) {
            const localizedName = getLocalizedFacilityNameFromCollection(
              highlightedFacility.id,
              data.scaledPolygons,
            );
            if (localizedName && highlightedFacility.name !== localizedName) {
              setHighlightedFacility((current) =>
                current && current.id === highlightedFacility.id
                  ? { ...current, name: localizedName }
                  : current,
              );
            }
          }
        }
      } catch (error) {
        if (lastAppliedLanguageRef.current === language) {
          lastAppliedLanguageRef.current = previousLanguage;
        }
        diagnostics.recordError(error, `language_change_failed:${language}`);
        console.error("言語変更時のマップデータ更新に失敗:", error);
      }
    };

    diagnostics.recordEvent("language_changed", language);
    refreshMapLabels();
  }, [
    getFloorGeoJsonPath,
    getLocalizedFacilityNameFromCollection,
    highlightedFacility,
    isMapLoaded,
    language,
    updateMapData,
  ]);

  useEffect(() => {
    if (!isMapLoaded) return;
    updateBuildingPointerLanguage(language);
  }, [isMapLoaded, language, updateBuildingPointerLanguage]);

  useEffect(() => {
    const mapInstance = map.current;
    const container = mapContainer.current;

    if (!isMapLoaded || !mapInstance || !container) {
      return;
    }

    let frameId: number | null = null;

    const scheduleMapResize = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        mapInstance.resize();
      });
    };

    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleMapResize) : null;

    resizeObserver?.observe(container);
    scheduleMapResize();

    window.addEventListener("resize", scheduleMapResize);
    window.addEventListener("orientationchange", scheduleMapResize);
    window.visualViewport?.addEventListener("resize", scheduleMapResize);
    window.visualViewport?.addEventListener("scroll", scheduleMapResize);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMapResize);
      window.removeEventListener("orientationchange", scheduleMapResize);
      window.visualViewport?.removeEventListener("resize", scheduleMapResize);
      window.visualViewport?.removeEventListener("scroll", scheduleMapResize);
    };
  }, [isMapLoaded, map]);

  useEffect(() => {
    if (!isMapLoaded) return;
    initializeCurrentLocation();
    updateBuildingPointers(currentFloorId);
  }, [isMapLoaded, initializeCurrentLocation, updateBuildingPointers, currentFloorId]);

  useEffect(() => {
    if (!isMapLoaded) return;
    bringRouteLayersToFront();
  }, [isMapLoaded, bringRouteLayersToFront]);

  useEffect(() => {
    const mapInstance = map.current;
    if (!isMapLoaded || !mapInstance) return;

    const targetLayers = ["rooms-labels", "rooms-labels-low"] as const;

    const applyLabelOpacity = (facilityId?: string | null) => {
      const opacityExpression: ExpressionSpecification | number = facilityId
        ? (["case", ["==", ["get", "id"], facilityId], 0, 1] as ExpressionSpecification)
        : 1;

      targetLayers.forEach((layerId) => {
        if (mapInstance.getLayer(layerId)) {
          mapInstance.setPaintProperty(layerId, "text-opacity", opacityExpression);
        }
      });
    };

    if (isInfoModalOpen) {
      clearFacilityNameBubble();
      applyLabelOpacity();
      return;
    }

    if (highlightedFacility && highlightedFacility.floorId === currentFloorId) {
      displayFacilityNameBubble(
        highlightedFacility.id,
        highlightedFacility.name,
        latestPolygonsRef.current,
      );

      applyLabelOpacity(highlightedFacility.id);
    } else {
      clearFacilityNameBubble();

      applyLabelOpacity();
    }
  }, [
    highlightedFacility,
    currentFloorId,
    isMapLoaded,
    isInfoModalOpen,
    displayFacilityNameBubble,
    clearFacilityNameBubble,
    map,
  ]);
  useEffect(() => {
    if (isMapLoaded && pendingFloorChange && !floorChangeInProgressRef.current) {
      handleFloorChange(pendingFloorChange);
    }
  }, [isMapLoaded, pendingFloorChange, handleFloorChange]);
  const handleStartDefaultRouteSearch = async (goalItem: MetadataItem) => {
    await handleSignageRouteSearch(goalItem, "default");
  };
  const handleStartBarrierFreeRouteSearch = async (goalItem: MetadataItem) => {
    await handleSignageRouteSearch(goalItem, "barrier-free");
  };
  const [selectedBuilding, floorStr] = currentFloorId.split("-");
  const selectedFloor = floorStr ? parseInt(floorStr, 10) : null;
  const handleFloorChangeFromSidebar = async (buildingId: string, floor: number): Promise<void> => {
    const newFloorId = `${buildingId}-${floor}`;
    await handleFloorChange(newFloorId);
  };

  return (
    <div className="relative flex h-dvh flex-col sm:flex-row">
      {/* data-scr: 画面別タップ集計の帰属先。diagnostics が pointerdown capture で closest() 参照 */}
      <div data-scr="sidebar" className="contents">
        <Sidebar
          selectedBuilding={selectedBuilding}
          selectedFloor={selectedFloor}
          onFloorChange={handleFloorChangeFromSidebar}
          onStartRouteSearch={handleStartDefaultRouteSearch}
          onStartBarrierFreeRouteSearch={handleStartBarrierFreeRouteSearch}
          onRouteClear={handleRouteClear}
          onResetMap={handleResetMap}
          facilityIdToSelect={selectedFacilityId}
          currentRoutingMode={currentRoutingMode}
          hasAlternativeRoute={hasAlternativeRoute}
          sharedRouteRequest={sharedRouteRequest}
          shareUrl={shareUrl}
          isShareUrlLoading={isShareUrlLoading}
          onFocusOnFacility={handleFocusOnFacility}
          onRestoreCameraPosition={handleRestoreCameraPosition}
          onHighlightFacility={handleHighlightFacility}
          onClearHighlight={handleClearHighlight}
          isMobile={isMobile}
          isKioskMode={KIOSK_MODE}
          currentHeight={sidebarHeight}
          onHeightChange={setSidebarHeight}
          onOpenInfo={() => {
            setIsInfoModalOpen(true);
            diagnostics.recordEvent("info_modal_opened");
          }}
          kioskResetKey={kioskResetKey}
          onMobileDestButtonVisibleChange={setShowMobileDestButton}
          mobileDestClickRef={mobileDestClickRef}
        />
      </div>
      <main data-scr="map" className="relative flex-1">
        <h1 className="sr-only">{t("FINDS｜未来大キャンパスマップ")}</h1>
        <div ref={mapContainer} className="h-full w-full" />

        {!isMobile && (
          <>
            <div data-scr="lang" className="contents">
              <LanguageSelector
                className="pointer-events-auto absolute left-4 top-4 z-50"
                resetKey={kioskResetKey}
              />
            </div>
            <div className="pointer-events-none absolute inset-0 z-50">
              {/* フロアセレクター: 右側・縦中央 */}
              <div
                data-scr="floor"
                className="pointer-events-auto absolute right-4 top-1/2 -translate-y-1/2"
              >
                <MapFloorSelector
                  currentFloorId={currentFloorId}
                  floors={availableFloors}
                  onFloorChange={handleFloorChange}
                  isMobile={isMobile}
                />
              </div>
              {/* Shortcut: 下部中央 */}
              <div
                data-scr="shortcut"
                className="pointer-events-auto absolute bottom-6 left-4 flex justify-center"
                style={{ right: `${DESKTOP_LOCATION_BUTTON_RESERVED_WIDTH}px` }}
              >
                <Shortcut
                  onSelect={handleShortcutSelect}
                  isMobile={isMobile}
                  isRouteActive={isRouteActive}
                  onBlockedSelect={showRouteActiveToast}
                />
              </div>
              {/* 現在地ボタン: 右下 — Shortcutと同じbottom-6で下辺を確実に揃える */}
              <div
                data-scr="location"
                className="pointer-events-auto absolute right-4 bottom-6 flex"
              >
                <LocationButton onClick={handleResetMap} className="justify-center" />
              </div>
            </div>
          </>
        )}

        {isMobile && (
          <>
            {/* SP統合トップバー: 目的地/LANG/Floorを同一flexコンテナに収める */}
            <div className="pointer-events-none fixed left-4 right-4 top-4 z-[55] flex items-start gap-3">
              {showMobileDestButton && (
                <button
                  type="button"
                  onClick={() => mobileDestClickRef.current?.()}
                  className="pointer-events-auto inline-flex h-[72px] min-w-0 flex-1 items-center justify-center gap-3 rounded-2xl border border-Border bg-white px-4 text-TextPrimary transition-colors hover:bg-Background"
                >
                  <MdSearch className="shrink-0 text-[24px]" />
                  <Text as="span" variant="body/m" weight="bold" className="truncate leading-none">
                    {t("目的地を選ぶ")}
                  </Text>
                </button>
              )}
              <div className="pointer-events-auto ml-auto flex flex-shrink-0 items-start gap-3">
                <LanguageSelector resetKey={kioskResetKey} isMobile />
                <MapFloorSelectorButtonSm
                  className="relative"
                  selectedBuilding={selectedBuilding}
                  selectedFloor={selectedFloor}
                  onFloorChange={handleFloorChangeFromSidebar}
                />
              </div>
            </div>

            <div className="fixed bottom-6 right-4 z-50 flex">
              <LocationButton onClick={handleResetMap}>{t("正面玄関に戻る")}</LocationButton>
            </div>
          </>
        )}

        {!isMapLoaded && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingContent}>
              <div className={styles.loadingSpinner} />
              <p>{t("マップを読み込んでいます...")}</p>
            </div>
          </div>
        )}

        <div data-scr="info" className="contents">
          <InformationModal
            isOpen={isInfoModalOpen}
            onClose={() => setIsInfoModalOpen(false)}
            language={language}
            isMobile={isMobile}
          />
        </div>

        <Toast
          message={toastMessage}
          className={
            isMobile
              ? "fixed left-4 right-4 top-28 z-[65] flex justify-center"
              : "absolute bottom-36 left-4 right-4 z-[65] flex justify-center"
          }
        />

        {diagnosticsTrigger.isOpen && <DebugDiagnosticsPanel onClose={diagnosticsTrigger.close} />}
      </main>

      {/* data-scr の外: 予告へのタップは "other" に集計 */}
      {kioskResetSecondsLeft !== null && (
        <KioskResetWarning
          secondsLeft={kioskResetSecondsLeft}
          onContinue={dismissKioskResetWarning}
        />
      )}
    </div>
  );
}
